import React, { useEffect, useState } from 'react';
import { Chip, Popover } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';

import type { AccessibilityFix, AccessibilityIssue } from '../utils/accessibilityChecker';
import { worstSeverity } from '../utils/accessibilityChecker';

import AccessibilityPanel from './AccessibilityPanel';

export interface AccessibilityChipProps {
  issues: AccessibilityIssue[];
  onApplyFix?: (issue: AccessibilityIssue, fix: AccessibilityFix) => void;
  onSelectElement?: (elementId: string) => void;
}

/**
 * Floating chip rendered above the slide canvas. Hidden when the current slide
 * has no issues. Clicking opens a popover with the issue panel.
 */
const AccessibilityChip: React.FC<AccessibilityChipProps> = ({
  issues,
  onApplyFix,
  onSelectElement,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const severity = worstSeverity(issues);

  useEffect(() => {
    if (issues.length === 0) {
      setAnchorEl(null);
    }
  }, [issues.length]);

  if (severity === null) return null;

  const isFail = severity === 'fail';
  const count = issues.length;
  const label = `${count} accessibility ${count === 1 ? 'issue' : 'issues'}`;

  return (
    <>
      <Chip
        icon={isFail ? <ErrorOutlineIcon /> : <WarningAmberIcon />}
        label={label}
        color={isFail ? 'error' : 'warning'}
        variant="filled"
        size="small"
        clickable
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{ boxShadow: 1 }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <AccessibilityPanel
          issues={issues}
          onApplyFix={(issue, fix) => {
            onApplyFix?.(issue, fix);
            // Leave popover open so the user can review remaining issues; the list
            // recomputes each render so resolved entries disappear naturally.
          }}
          onSelectElement={(elementId) => {
            onSelectElement?.(elementId);
            setAnchorEl(null);
          }}
        />
      </Popover>
    </>
  );
};

export default AccessibilityChip;
